import axios from 'axios';

const INITIAL_STATE = () => ({
  isLoading: false,
  overview: [],
  weekends: [],
  serviceReplay: {},
  serviceReplays: {},
  passcode: '',
  access: null,
  defaultReplayId: '',
  hasEnteredPassword: false,
});

const state = () => INITIAL_STATE();

const getDefaultReplayId = weekends => {
  if (!Array.isArray(weekends)) return '';

  const weekendWithReplay = weekends.find(weekend => weekend.hogc_one_replays?.length > 0);
  return weekendWithReplay?.hogc_one_replays?.[0]?.id ?? '';
};

const getPasscodeValue = data =>
  data?.passcode ??
  data?.Passcode ??
  data?.attributes?.passcode ??
  data?.attributes?.Passcode ??
  '';

const mutations = {
  setIsLoading(state, isLoading) {
    state.isLoading = isLoading;
  },
  setOverview(state, overview) {
    state.overview = overview;
  },
  setWeekends(state, weekends) {
    state.weekends = weekends;
  },
  setServiceReplay(state, newServiceReplay) {
    state.serviceReplay = newServiceReplay;
  },
  addServiceReplay(state, newServiceReplay) {
    state.serviceReplays[newServiceReplay.serviceReplayId] = newServiceReplay;
  },
  setPasscode(state, passcode) {
    state.passcode = passcode;
  },
  setAccess(state, access) {
    state.access = access;
  },
  setDefaultReplayId(state, id) {
    state.defaultReplayId = id;
  },
  setEnteredPassword(state, entered) {
    state.hasEnteredPassword = entered;
  },
};

const actions = {
  async fetchOverview({ commit, dispatch, rootState }) {
    commit('setIsLoading', true);
    try {
      const accessToken = rootState.user.token;
      const { data } = await axios.get(
        process.env.VUE_APP_API_URL + '/cms/service-replay/overview',
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );
      commit('setOverview', Array.isArray(data) ? data : []);
    } catch (e) {
      dispatch('toast/error', 'Failed to fetch service replay overview', { root: true });
    } finally {
      commit('setIsLoading', false);
    }
  },
  async fetchPasscode({ commit, dispatch, rootState }) {
    commit('setIsLoading', true);
    try {
      const accessToken = rootState.user.token;
      const { data } = await axios.get(process.env.VUE_APP_API_URL + '/servicereplay/passcode', {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      commit('setPasscode', getPasscodeValue(data));
    } catch (e) {
      dispatch('toast/error', 'Failed to fetch service replay passcode', { root: true });
    } finally {
      commit('setIsLoading', false);
    }
  },
  async fetchAccess({ commit, dispatch, rootState }) {
    commit('setIsLoading', true);
    try {
      const accessToken = rootState.user.token;
      const { data } = await axios.get(process.env.VUE_APP_API_URL + '/servicereplay/access', {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      commit('setAccess', data);
      return data;
    } catch (e) {
      dispatch('toast/error', 'Failed to check service replay access', { root: true });
      return null;
    } finally {
      commit('setIsLoading', false);
    }
  },
  async fetchWeekends({ commit, dispatch, rootState }) {
    commit('setIsLoading', true);
    try {
      const accessToken = rootState.user.token;
      const { data } = await axios.get(
        process.env.VUE_APP_API_URL + '/cms/service-replay/weekend',
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );
      const weekends = Array.isArray(data) ? data : [];
      commit('setDefaultReplayId', getDefaultReplayId(weekends));
      commit('setWeekends', weekends);
    } catch (e) {
      dispatch('toast/error', 'Failed to fetch service replay weekends', { root: true });
    } finally {
      commit('setIsLoading', false);
    }
  },
  async fetchServiceReplayById({ state, commit, dispatch, rootState }, { serviceReplayId }) {
    if (!serviceReplayId || state.serviceReplays[serviceReplayId]) return;

    commit('setIsLoading', true);
    try {
      const accessToken = rootState.user.token;
      const { data } = await axios.get(
        process.env.VUE_APP_API_URL + '/cms/service-replay/' + serviceReplayId,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );
      commit('addServiceReplay', data);
    } catch (e) {
      dispatch('toast/error', 'Failed to fetch service replay overview', { root: true });
    } finally {
      commit('setIsLoading', false);
    }
  },
  async passwordEntered({ commit, dispatch }) {
    try {
      commit('setEnteredPassword', true);
    } catch {
      dispatch('toast/error', 'Failed to update password status', { root: true });
    }
  },
  async verifyPasscode({ commit, dispatch, rootState }, passcode) {
    try {
      const accessToken = rootState.user.token;
      const { data } = await axios.post(
        process.env.VUE_APP_API_URL + '/servicereplay/passcode/verify',
        { passcode },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );
      const isValid = Boolean(data?.valid);
      if (isValid) commit('setEnteredPassword', true);
      return isValid;
    } catch {
      dispatch('toast/error', 'Failed to verify service replay passcode', { root: true });
      return false;
    }
  },
};

const getters = {
  isLoading(state) {
    return state.isLoading;
  },
  overview(state) {
    return state.overview;
  },
  serviceReplays(state) {
    return state.serviceReplays;
  },
};

export default {
  namespaced: true,
  state,
  actions,
  getters,
  mutations,
};
